import { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from '@mui/material';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  NoDataSkeleton,
  ActionIconButton,
  TableBodySkeleton,
} from '../../../common';

const UsersDataTable = ({ data, isLoading, onEdit, onDelete, LoadingComponent, roles = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const roleMap = useMemo(() => {
    return roles.reduce((acc, role) => {
      if (role?.uuid) {
        acc[role.uuid] = role.name;
      }
      return acc;
    }, {});
  }, [roles]);

  const getRoleName = (user) => {
    if (!user) {
      return 'Sin rol';
    }

    const uuidRol = user.uuid_rol || user.uuidRol;
    return (uuidRol && roleMap[uuidRol]) || 'Sin rol';
  };

  const getFirstName = (fullName) => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  if (isMobile) {
    return (
      <GeneralTableBox>
        {isLoading ? (
          <TableBodySkeleton variant="box" />
        ) : data && data.length > 0 ? (
          <Box sx={{ p: 2 }}>
            {data.map((user, index) => (
              <Box
                key={user.uuid || index}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2,
                  backgroundColor: 'white',
                }}
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Usuario
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {getFirstName(user.names)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Nombre
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {user.names}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Teléfono
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {user.telephone || 'Sin teléfono'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Rol
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {getRoleName(user)}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2" fontWeight="medium" sx={{ mb: 2 }}>
                    {user.email || 'Sin email'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <ActionIconButton variant="edit" onClick={() => onEdit(user)} />
                  <ActionIconButton variant="delete" onClick={() => onDelete(user.uuid)} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <NoDataSkeleton colSpan={1} message="No se encontraron usuarios" />
        )}
      </GeneralTableBox>
    );
  }

  return (
    <GeneralTableBox>
      <Table aria-label="users table">
        <TableHead>
          <TableRow>
            <HeadTableCell wmd="15%" wxs="20%">
              Usuario
            </HeadTableCell>
            <HeadTableCell wmd="20%" wxs="25%">
              Nombre
            </HeadTableCell>
            <HeadTableCell wmd="15%" wxs="15%">
              Teléfono
            </HeadTableCell>
            <HeadTableCell wmd="25%" wxs="25%">
              Email
            </HeadTableCell>
            <HeadTableCell wmd="10%" wxs="10%">
              Rol
            </HeadTableCell>
            <HeadTableCell wmd="15%" wxs="5%">
              Acciones
            </HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <LoadingComponent />
          ) : data && data.length > 0 ? (
            data.map((user, index) => (
              <TableRow key={user.uuid || index}>
                <RowTableCell fontxs="12px" fontmd="14px">
                  {getFirstName(user.names)}
                </RowTableCell>
                <RowTableCell fontxs="12px" fontmd="14px">
                  {user.names}
                </RowTableCell>
                <RowTableCell fontxs="12px" fontmd="14px">
                  {user.telephone || 'Sin teléfono'}
                </RowTableCell>
                <RowTableCell fontxs="12px" fontmd="14px">
                  {user.email}
                </RowTableCell>
                <RowTableCell fontxs="12px" fontmd="14px">
                  {getRoleName(user)}
                </RowTableCell>
                <RowTableCell sx={{ textAlign: 'center' }}>
                  <ActionIconButton variant="edit" onClick={() => onEdit(user)} />
                  <ActionIconButton variant="delete" onClick={() => onDelete(user.uuid)} />
                </RowTableCell>
              </TableRow>
            ))
          ) : (
            <NoDataSkeleton colSpan={6} message="No se encontraron usuarios" />
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default UsersDataTable;
